import React, { useCallback, useState } from 'react';
import { Box, Checkbox, FormControlLabel, Grid, Paper, TextField, Typography } from '@mui/material';
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import ErpFieldTree from './ErpFieldTree';
import MarminDropTree from './MarminDropTree';
import FieldLegoChip from './FieldLegoChip';
import {
  clearArraySource,
  clearLeafMapping,
  setArraySource,
  setLeafDefault,
  setLeafSource,
} from '../../utils/fieldsTreeMutations';
import { findErpFieldNode } from '../../utils/erpFieldTree';
import { resolveMappedSourcePath } from '../../utils/arrayMappingPaths';

const paneSx = {
  p: 2,
  height: 'min(70vh, 720px)',
  overflowY: 'auto',
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 1,
};

const SimpleMappingBoard = ({ samplePayload, fieldCatalog, fields, onFieldsChange, disabled }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [marminSearchQuery, setMarminSearchQuery] = useState('');
  const [requiredOnly, setRequiredOnly] = useState(false);
  const [selectedErpPath, setSelectedErpPath] = useState('');
  const [activeDragNode, setActiveDragNode] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor),
  );

  const applyMapping = useCallback(
    (erpPath, marminPath, targetKind) => {
      if (!erpPath || !marminPath) {
        return;
      }

      const erpNode = findErpFieldNode(samplePayload, erpPath);
      if (!erpNode) {
        return;
      }

      const sourcePath = resolveMappedSourcePath({
        erpPath,
        marminPath,
        fieldCatalog,
        fields,
      });

      if (targetKind === 'array') {
        if (erpNode.kind !== 'array') {
          return;
        }

        onFieldsChange(setArraySource(fields, marminPath, sourcePath));
      } else {
        if (erpNode.kind === 'object') {
          return;
        }

        onFieldsChange(setLeafSource(fields, marminPath, sourcePath));
      }

      setSelectedErpPath('');
    },
    [fieldCatalog, fields, onFieldsChange, samplePayload],
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveDragNode(null);

    if (!over || !active?.data?.current) {
      return;
    }

    const erpData = active.data.current;
    const overData = over.data.current;

    if (erpData.type !== 'erp' || !overData?.path) {
      return;
    }

    if (overData.type === 'marmin-array') {
      applyMapping(erpData.path, overData.path, 'array');

      return;
    }

    if (overData.type === 'marmin-leaf') {
      applyMapping(erpData.path, overData.path, 'field');
    }
  };

  const handleSetDefault = (path, value) => {
    onFieldsChange(setLeafDefault(fields, path, value));
  };

  const handleClearLeaf = (path) => {
    onFieldsChange(clearLeafMapping(fields, path));
  };

  const handleClearArray = (path) => {
    onFieldsChange(clearArraySource(fields, path));
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={(event) => {
        const erpPath = event.active?.data?.current?.path;
        if (erpPath) {
          setActiveDragNode(findErpFieldNode(samplePayload, erpPath));
        }
      }}
      onDragEnd={handleDragEnd}
      onDragCancel={() => setActiveDragNode(null)}
    >
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Drag ERP fields onto Marmin targets, or select an ERP field then click a Marmin drop zone. For arrays, map the
        array path first (e.g. $.lines), then map item fields shown underneath (e.g. $.name). Set static values with the
        Default input on each Marmin field.
      </Typography>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
            ERP fields
          </Typography>
          <TextField
            fullWidth
            size="small"
            label="Search ERP fields"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            sx={{ mb: 1 }}
            disabled={disabled}
          />
          <Paper variant="outlined" sx={paneSx}>
            <ErpFieldTree
              samplePayload={samplePayload}
              searchQuery={searchQuery}
              selectedPath={selectedErpPath}
              onSelectPath={setSelectedErpPath}
              disabled={disabled}
            />
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <StackControls
            marminSearchQuery={marminSearchQuery}
            setMarminSearchQuery={setMarminSearchQuery}
            requiredOnly={requiredOnly}
            setRequiredOnly={setRequiredOnly}
            disabled={disabled}
          />
          <Paper variant="outlined" sx={paneSx}>
            <MarminDropTree
              fieldCatalog={fieldCatalog}
              fields={fields}
              searchQuery={marminSearchQuery}
              requiredOnly={requiredOnly}
              selectedErpPath={selectedErpPath}
              onMapErpToMarmin={applyMapping}
              onSetDefault={handleSetDefault}
              onClearLeaf={handleClearLeaf}
              onClearArray={handleClearArray}
              disabled={disabled}
            />
          </Paper>
        </Grid>
      </Grid>

      <DragOverlay>{activeDragNode ? <FieldLegoChip node={activeDragNode} disabled /> : null}</DragOverlay>
    </DndContext>
  );
};

const StackControls = ({ marminSearchQuery, setMarminSearchQuery, requiredOnly, setRequiredOnly, disabled }) => (
  <Box sx={{ mb: 1 }}>
    <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
      Marmin fields
    </Typography>
    <TextField
      fullWidth
      size="small"
      label="Search Marmin fields"
      value={marminSearchQuery}
      onChange={(event) => setMarminSearchQuery(event.target.value)}
      sx={{ mb: 1 }}
      disabled={disabled}
    />
    <FormControlLabel
      control={
        <Checkbox
          checked={requiredOnly}
          onChange={(event) => setRequiredOnly(event.target.checked)}
          disabled={disabled}
        />
      }
      label="Required only"
    />
  </Box>
);

export default SimpleMappingBoard;
